import React, { useState, useEffect, useRef, useCallback } from 'react';
import { emailTemplatesApi } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { Loader2, RotateCcw, Save, Send, Mail, Check } from 'lucide-react';

const EmailTemplatesManager = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedKey, setSelectedKey] = useState(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [preview, setPreview] = useState({ subject: '', html: '' });
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const bodyRef = useRef(null);

  const selected = list.find((t) => t.key === selectedKey) || null;

  const pick = (t) => {
    setSelectedKey(t.key);
    setSubject(t.subject || '');
    setBody(t.body_html || '');
    setDirty(false);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await emailTemplatesApi.list();
      const data = r.data || [];
      setList(data);
      if (data.length) {
        setSelectedKey((prev) => prev || data[0].key);
        if (!selectedKey && data[0]) {
          setSubject(data[0].subject || '');
          setBody(data[0].body_html || '');
        }
      }
    } catch (e) {
      toast.error('Failed to load email templates');
    } finally {
      setLoading(false);
    }
  }, [selectedKey]);

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced live preview
  useEffect(() => {
    if (!selectedKey) return;
    const id = setTimeout(async () => {
      try {
        const r = await emailTemplatesApi.preview(selectedKey, { subject, body_html: body });
        setPreview(r.data || { subject: '', html: '' });
      } catch (e) { /* ignore preview errors */ }
    }, 400);
    return () => clearTimeout(id);
  }, [selectedKey, subject, body]);

  const refreshRow = (row) => setList((l) => l.map((t) => (t.key === row.key ? row : t)));

  const insertField = (name) => {
    const token = `{{${name}}}`;
    const ta = bodyRef.current;
    if (!ta) { setBody((b) => b + token); setDirty(true); return; }
    const start = ta.selectionStart, end = ta.selectionEnd;
    setBody(body.slice(0, start) + token + body.slice(end));
    setDirty(true);
    requestAnimationFrame(() => { ta.focus(); ta.selectionStart = ta.selectionEnd = start + token.length; });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await emailTemplatesApi.save(selectedKey, { subject, body_html: body });
      toast.success('Template saved');
      setDirty(false);
      refreshRow(r.data);
    } catch (e) { toast.error('Failed to save template'); }
    finally { setSaving(false); }
  };

  const handleReset = async () => {
    if (!window.confirm('Restore this email to the built-in default? Your custom version will be discarded.')) return;
    try {
      const r = await emailTemplatesApi.reset(selectedKey);
      toast.success('Restored to default');
      setSubject(r.data.subject || '');
      setBody(r.data.body_html || '');
      setDirty(false);
      refreshRow(r.data);
    } catch (e) { toast.error('Failed to restore default'); }
  };

  const handleTest = async () => {
    const to = window.prompt('Send a test of this email to which address?');
    if (!to) return;
    try {
      await emailTemplatesApi.test(selectedKey, { to, subject, body_html: body });
      toast.success('Test email sent');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send test email'); }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
      {/* Template list */}
      <div className="card-hive p-3 h-fit">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground px-2 py-1.5">Emails</p>
        <div className="space-y-1">
          {list.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => pick(t)}
              className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${selectedKey === t.key ? 'bg-primary/10 border border-primary/30' : 'hover:bg-secondary/50 border border-transparent'}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium truncate">{t.label}</span>
                {t.is_customised && <span className="text-[10px] text-primary bg-primary/10 px-1.5 py-0.5 rounded-full flex-shrink-0">Custom</span>}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Editor + preview */}
      {selected && (
        <div className="space-y-4">
          <div className="card-hive p-5">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <h3 className="font-heading font-semibold">{selected.label}</h3>
                <p className="text-sm text-muted-foreground">{selected.description}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button variant="outline" size="sm" onClick={handleTest}><Send className="w-4 h-4 mr-1.5" /> Test</Button>
                <Button variant="outline" size="sm" onClick={handleReset} disabled={!selected.is_customised}><RotateCcw className="w-4 h-4 mr-1.5" /> Restore default</Button>
                <Button size="sm" className="btn-primary" onClick={handleSave} disabled={saving || !dirty}>
                  {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />} Save
                </Button>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground">Subject</label>
                <Input value={subject} onChange={(e) => { setSubject(e.target.value); setDirty(true); }} className="input-hive mt-1" />
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium text-muted-foreground">Body</label>
                  <span className="text-[11px] text-muted-foreground">HTML supported</span>
                </div>
                <div className="flex flex-wrap gap-1.5 my-2">
                  <span className="text-[11px] text-muted-foreground self-center mr-1">Insert field:</span>
                  {selected.fields.map((f) => (
                    <button key={f.name} type="button" onClick={() => insertField(f.name)}
                      title={f.label}
                      className="text-[11px] font-mono px-2 py-0.5 rounded-md bg-secondary hover:bg-primary/15 hover:text-primary transition-colors">
                      {`{{${f.name}}}`}
                    </button>
                  ))}
                </div>
                <textarea
                  ref={bodyRef}
                  value={body}
                  onChange={(e) => { setBody(e.target.value); setDirty(true); }}
                  spellCheck={false}
                  className="w-full h-56 font-mono text-xs p-3 rounded-lg border border-border bg-background resize-y focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>
            </div>
          </div>

          {/* Live preview */}
          <div className="card-hive p-5">
            <div className="flex items-center gap-2 mb-3">
              <Mail className="w-4 h-4 text-primary" />
              <h4 className="font-heading font-semibold text-sm">Live preview</h4>
              <span className="text-xs text-muted-foreground">(sample data)</span>
            </div>
            <div className="rounded-lg border border-border overflow-hidden">
              <div className="px-4 py-2 bg-secondary/40 border-b border-border text-sm">
                <span className="text-muted-foreground">Subject: </span>
                <span className="font-medium">{preview.subject || '—'}</span>
              </div>
              <iframe title="email-preview" srcDoc={preview.html} className="w-full bg-white" style={{ height: 440, border: 0 }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailTemplatesManager;
